/**
 * Crosswalk — the ONE file to edit when repurposing this poll tracker for a
 * different election. Everything else in `src/lib/` reads from here; nothing
 * else should hardcode a party name, field name, colour, or threshold.
 *
 * To reuse this component for another country/election:
 *   1. Replace `src/lib/data/all-polls.json` with the new dataset.
 *   2. Update `dataConfig` to match that dataset's column names.
 *   3. Set `valueType` ('percent' | 'seats') and `seatConfig`/`threshold` as needed.
 *   4. Rebuild `parties` for the new dataset's party columns.
 *   5. Update `smartTextConfig` once real narrative copy exists in rngs.io.
 *
 * Colours live in `./colors.ts`, shared with `HistoricalElections/party-meta.js` —
 * edit there to change a party's colour everywhere it appears.
 */

import { blocColors, darken } from './colors';

/**
 * Political bloc each party belongs to, matching `blocColors`/`blocMeta` in
 * `./colors.ts` and `HistoricalElections/party-meta.js`. Drives this file's
 * derived `color` below, keeping the poll tracker on the same bloc palette as
 * the historical chart.
 *
 * `erdan_edelstein_party` has no clear real-world bloc — a best-guess
 * placement, review with an editor. `reservists` and `people_of_israel` are
 * newer columns without a curated real-world party name yet. both are
 * bucketed as `other` (gray) until more polling clarifies which bloc they
 * poll as. PollChart draws any party under
 * `LOW_SEAT_THRESHOLD` seats in gray regardless of bloc (see `constants/index.ts`).
 */
const partyBlocs: Record<string, keyof typeof blocColors> = {
  likud: 'likud',
  united_torah_judaism: 'ultraOrthodox',
  shas: 'ultraOrthodox',
  blue_and_white: 'centre',
  there_is_a_future: 'centre',
  israel_our_home: 'right',
  democrats: 'left',
  religious_zionism_identity: 'farRight',
  raam: 'arab',
  jewish_power: 'farRight',
  together: 'centre',
  yashar: 'centre',
  reservists: 'other',
  joint_list: 'arab',
  hadash_taal: 'arab',
  balad: 'arab',
  erdan_edelstein_party: 'likud', // guess — review
  people_of_israel: 'other',
};

/**
 * Colour for each party key: the first party (in `parties` order) in a given
 * bloc gets that bloc's full colour; each later party sharing the same bloc
 * gets progressively darkened so they read as muted variants rather than
 * identical swatches.
 *
 * `together`/`yashar` are a pair within the centre bloc — restart their own
 * shade count so Together gets the full colour and Yashar is just one shade
 * darker, rather than inheriting Blue and White/Yesh Atid's position further
 * down the bloc's shade chain.
 */
const colorVariantOverride: Partial<Record<string, number>> = {
  together: 0,
  yashar: 1,
};

const blocSeen = new Set<string>();
const colorFor = (key: string): string => {
  const bloc = partyBlocs[key];
  const base = blocColors[bloc];
  const idx =
    colorVariantOverride[key] ??
    [...blocSeen].filter((seenKey) => partyBlocs[seenKey] === bloc).length;
  blocSeen.add(key);
  return darken(base, idx);
};

export type ValueType = 'percent' | 'seats';

export interface PartyConfig {
  /** Raw column name in the poll data (e.g. `likud`). */
  key: string;
  /** English display label. */
  label: string;
  /** Hex colour used for this party's line/dots/labels. */
  color: string;
  /** CSS-safe slug, used for classnames/data attributes. */
  slug: string;
}

/** Field-name mapping for the raw poll records in `all-polls.json`. */
export const dataConfig = {
  dateField: 'date',
  /**
   * Polls before this date are dropped — the tracker only covers the
   * current campaign, which effectively starts here (the dataset's older
   * election cycles all predate it).
   */
  startDate: '2024-01-01',
  pollsterField: 'pollster_en',
  mediaOutletField: 'media_outlet_en',
  respondentsField: 'respondents',
  sourceUrlField: 'source_url',
  auditFlagsField: 'audit_flags',
  /**
   * Poll records carrying any of these flags are dropped entirely before
   * charting — these mark exact duplicate payloads, not just statistical
   * outliers, so they'd double-count a single poll if left in.
   */
  excludeAuditFlags: [
    'exact_payload_duplicate_group_1',
    'exact_payload_duplicate_group_2',
  ],
  /**
   * Media outlets dropped entirely from the dataset — both the trend-line
   * calculations and the media-outlet filter nav. Add or remove a name here
   * (must match `media_outlet_en` exactly) to hide/show an outlet.
   */
  excludeMediaOutlets: ['The Truth Machine', 'Channel 16', 'Walla'],
};

/**
 * This dataset reports seat projections, not vote share, so there is no
 * respondent-driven margin of error to plot (MOE is a vote-share/sample-size
 * concept). If a future dataset reports percent instead, switch this to
 * 'percent' and re-enable the MOE band in `data/summarize.ts`.
 */
export const valueType: ValueType = 'seats';

export const seatConfig = {
  /** Seats in the Knesset. */
  totalSeats: 120,
};

/**
 * Israel's electoral threshold is a share of the national vote, not a seat
 * count — parties must clear 3.25% of votes cast to win any Knesset seats.
 * Since this dataset is in seats, we convert: 3.25% of 120 seats ≈ 3.9 seats.
 * `getThresholdValue()` below does that conversion generically so this stays
 * correct if `valueType` or `totalSeats` ever change.
 */
export const threshold = {
  votePercent: 3.25,
  label:
    "Parties must clear Israel's 3.25% electoral threshold — roughly 3.9 of the Knesset's 120 seats — to win any seats in parliament.",
};

export const getThresholdValue = (): number => {
  if (valueType === 'seats') {
    return (threshold.votePercent / 100) * seatConfig.totalSeats;
  }
  return threshold.votePercent;
};

/**
 * Auto-derived from the 16 party columns found in `all-polls.json`, in the
 * data's own column order. NOT curated — review before publishing:
 *
 * - Labels are mechanical title-cases of the column key (a few obvious
 *   transliterations fixed by hand, e.g. "Ra'am"). Several likely need a
 *   real party/list name.
 * - `joint_list` sums `hadash_taal` and `balad` into one figure for the
 *   polls that report it that way (also mapped from the Hebrew header
 *   "הרשימה המשותפת"); other polls still report `hadash_taal`/`balad`
 *   separately, so all three are tracked as their own party/line here.
 * - `reservists` and `people_of_israel` are newer columns (replacing the
 *   former `hendel_zelicha_party`/`ofer_winter` placeholders) — no confirmed
 *   real-world party name yet, so the label is just the column's own name.
 * - `democrats` replaces the former `the_democrats` column name.
 * - `religious_zionism_identity` carries the same bloc/lineage as the old
 *   `religious_zionism` column; the label below may need updating once an
 *   editor confirms whether the list's name itself has changed.
 */
export const parties: PartyConfig[] = [
  { key: 'likud', label: 'Likud', color: colorFor('likud'), slug: 'likud' },
  {
    key: 'united_torah_judaism',
    label: 'United Torah Judaism',
    color: colorFor('united_torah_judaism'),
    slug: 'united-torah-judaism',
  },
  { key: 'shas', label: 'Shas', color: colorFor('shas'), slug: 'shas' },
  {
    key: 'blue_and_white',
    label: 'Blue and White',
    color: colorFor('blue_and_white'),
    slug: 'blue-and-white',
  },
  {
    key: 'there_is_a_future',
    label: 'Yesh Atid',
    color: colorFor('there_is_a_future'),
    slug: 'there-is-a-future',
  },
  {
    key: 'israel_our_home',
    label: 'Israel Our Home',
    color: colorFor('israel_our_home'),
    slug: 'israel-our-home',
  },
  {
    key: 'democrats',
    label: 'Democrats',
    color: colorFor('democrats'),
    slug: 'democrats',
  },
  // Secondary tier — bloc colour, muted where it shares a bloc, see note above.
  {
    key: 'religious_zionism_identity',
    label: 'Religious Zionism',
    color: colorFor('religious_zionism_identity'),
    slug: 'religious-zionism-identity',
  },
  {
    key: 'raam',
    label: "Ra'am",
    color: colorFor('raam'),
    slug: 'raam',
  },
  {
    key: 'jewish_power',
    label: 'Jewish Power',
    color: colorFor('jewish_power'),
    slug: 'jewish-power',
  },
  {
    key: 'together',
    label: 'Together',
    color: colorFor('together'),
    slug: 'together',
  },
  {
    key: 'yashar',
    label: 'Yashar',
    color: colorFor('yashar'),
    slug: 'yashar',
  },
  {
    key: 'reservists',
    label: 'Reservists',
    color: colorFor('reservists'),
    slug: 'reservists',
  },
  {
    key: 'joint_list',
    label: 'Joint List',
    color: colorFor('joint_list'),
    slug: 'joint-list',
  },
  {
    key: 'hadash_taal',
    label: "Hadash-Ta'al",
    color: colorFor('hadash_taal'),
    slug: 'hadash-taal',
  },
  {
    key: 'balad',
    label: 'Balad',
    color: colorFor('balad'),
    slug: 'balad',
  },
  {
    key: 'erdan_edelstein_party',
    label: 'Erdan-Edelstein Party',
    color: colorFor('erdan_edelstein_party'),
    slug: 'erdan-edelstein-party',
  },
  {
    key: 'people_of_israel',
    label: 'People of Israel',
    color: colorFor('people_of_israel'),
    slug: 'people-of-israel',
  },
];

/** Raw column names for every party, in data order. */
export const partyKeys: string[] = parties.map((p) => p.key);

/**
 * Per-party minimum date, keyed by `parties[].key` — trims a party's polling
 * to start display later than `dataConfig.startDate`, without dropping other
 * parties' values from the same poll record. Compared against the raw
 * `dataConfig.dateField` string (ISO `YYYY-MM-DD`), same as `startDate`.
 */
export const partyStartDates: Record<string, string> = {
  // Joint List readings from Jan-Apr 2026 ran far higher (11-16 seats) than
  // the level seen since combined Joint List polling resumed in late August
  // (5-9 seats) — an inconsistent early read, not a real swing. Only chart
  // Joint List from Aug 1 on; hadash_taal/balad (reported separately by
  // pollsters that don't poll a combined list) are unaffected.
  joint_list: '2026-08-01',
};

/**
 * Party keys temporarily hidden from the poll chart's lines/dots/hover only
 * — they're still counted everywhere else (the 2022 v. 2026 seat estimate,
 * smart text, etc.). Remove a key here once it's ready to show in the chart.
 */
export const hiddenFromPollChart: string[] = ['balad'];

/**
 * Drives SmartText's auto-generated narrative ("X gained N seats since...").
 * Both left null — there's no real Israel copy in rngs.io yet to reference
 * these variables, so there's nothing to compute towards. Once copy is
 * written, set e.g. `trackedGain: { key: 'likud', sinceDate: '2025-01-01' }`
 * and reference `{{{ trackedGainParty }}}`/`{{{ trackedGainValue }}}` in the
 * rngs.io template — see `src/lib/utils/index.ts#calculateSmartVars`.
 */
export const smartTextConfig: {
  trackedGain: { key: string; sinceDate: string } | null;
  trackedLoss: { key: string } | null;
} = {
  trackedGain: null,
  trackedLoss: null,
};
